import React from 'react';

import { capitalize } from 'lodash';

import { NavLinkButton, Table } from '@shared/components/bootstrap';
import { Timestamp, Retry } from '@shared/components/helpers';

import { Panel } from '@admin/components/helpers/panel';
import { Spinner } from '@admin/components/spinner';
import { warehouseTaskURL, newWarehouseTaskURL } from '@admin/config/routes';
import { usePermission } from '@admin/hooks/use_permission';
import { WAREHOUSE_OPS } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import { useWarehouseTasksQuery } from '@admin/schema';

import { STATE_LABELS, stateClass } from './constants';

export const WarehouseTasksAccountPanel: React.FC<{ accountID: string }> = ({ accountID }) => {
  const canReadTasks = usePermission(WAREHOUSE_OPS.resource, WAREHOUSE_OPS.actions.read);
  const canCreateTasks = usePermission(WAREHOUSE_OPS.resource, WAREHOUSE_OPS.actions.create);
  const { data, loading, error, refetch } = useWarehouseTasksQuery({
    client,
    variables: { filters: { accountID, per: 25 } },
    fetchPolicy: 'cache-and-network',
    skip: !canReadTasks,
  });

  if (!canReadTasks) {
    return null;
  }

  const tasks = data?.warehouseTasks?.results ?? [];

  return (
    <Panel>
      <Panel.Header>
        <Panel.Title>Warehouse Tasks</Panel.Title>
      </Panel.Header>
      <Panel.Body>
        {error ? (
          <Retry error={error} refetch={refetch} />
        ) : loading && !data ? (
          <Spinner />
        ) : (
          <Table responsive>
            <thead>
              <tr>
                <th>ID</th>
                <th>Status</th>
                <th>Created By</th>
                <th>Due Date</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task) => (
                <tr key={task.id}>
                  <td>
                    <a href={warehouseTaskURL({ id: task.id })}>#{task.id}</a>
                  </td>
                  <td>
                    <span className={`label ${stateClass(task.state)}`}>
                      {STATE_LABELS[task.state] || capitalize(task.state)}
                    </span>
                  </td>
                  <td>{task.creator.name}</td>
                  <td>{task.dueDate && <Timestamp value={task.dueDate} format={Timestamp.Format.Date} />}</td>
                  <td>
                    <Timestamp value={task.createdAt} format={Timestamp.Format.Date} />
                  </td>
                  <td>
                    <NavLinkButton kind="primary" to={warehouseTaskURL({ id: task.id })}>
                      View
                    </NavLinkButton>
                  </td>
                </tr>
              ))}
              {tasks.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-muted">
                    No warehouse tasks for this account
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </Panel.Body>
      {canCreateTasks && (
        <Panel.Footer align="right">
          <NavLinkButton kind="primary" to={newWarehouseTaskURL({ accountID })}>
            Create Warehouse Task
          </NavLinkButton>
        </Panel.Footer>
      )}
    </Panel>
  );
};
